/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/styles';
import Button from 'components/Button';
import { Form, Formik } from 'formik';
import i18n from 'i18n';
import {
  registerPasswordRequest,
  authCheckRestrictionsRequest,
} from 'modules/authenticate/actions';
import {
  PasswordConfirmationProps,
  RestrictionsProps,
} from 'modules/authenticate/interfaces';
import { RegisterPasswordRequestProps } from 'modules/authenticate/sagaInterfaces';
import React from 'react';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import GenericForm from 'skyone-lib/lib/GenericForm';
import { history } from 'store';
import ApplicationState from 'store/interfaces';
import { applyI18n } from 'utils/helpers';
import screenFields from './registerPasswordFormFields';
import validationSchema from './schema';
import styles from './styles';

const useStyles = makeStyles(styles);

const init: PasswordConfirmationProps = {
  password: '',
  confirm_password: '',
};

interface Props {
  isLoading: boolean;
  isSuccess: boolean;
  restrictions: RestrictionsProps;
  authCheckRestrictionsRequest: () => void;
  registerPasswordRequest: ({ payload }: RegisterPasswordRequestProps) => void;
}

const RegisterPasswordForm: React.FC<Props> = ({
  isLoading,
  isSuccess,
  restrictions,
  authCheckRestrictionsRequest,
  registerPasswordRequest,
}) => {
  const classes = useStyles();
  const { token } = useParams<{ token: string }>();
  const [restrictionsMap, setRestrictionsMap] = React.useState<
    Record<string, boolean>
  >({});

  const handleSubmit = values => {
    registerPasswordRequest({
      code: token,
      ...values,
    });
  };

  React.useEffect(() => {
    if (isSuccess) {
      history.push('/');
    }
  }, [isSuccess]);

  React.useEffect(() => {
    authCheckRestrictionsRequest();
  }, [authCheckRestrictionsRequest]);

  const checkRestrictions = (password: string) => {
    setRestrictionsMap({
      REQUIRE_UPPERCASE: !!password.match(/[A-Z]/g),
      REQUIRE_LOWERCASE: !!password.match(/[a-z]/g),
      REQUIRE_NUMBER: !!password.match(/\d/g),
      REQUIRE_SYMBOL: !!password.match(
        /[!@#$%¨&*()_+{}¹²³£¢¬§`´~^[\]:;,.<>ªº°?/\\|\-"'=]/g
      ),
      REQUIRE_MIN_LENGTH: password.trim().length >= restrictions.min_length,
      REQUIRE_MAX_LENGTH: password.trim().length <= restrictions.max_length,
    });
  };

  return (
    <Grid
      container
      item
      xs={12}
      alignContent="flex-start"
      className={classes.newPassword}
    >
      <Grid item container xs={12} className={classes.spacingContainer}>
        <Grid item xs={12} className={classes.login}>
          <Typography variant="h3" className={classes.text}>
            {i18n.t('REGISTER_PASSWORD_PAGE.TITLE_KEY')}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1" className={classes.subtitleLogin}>
            {i18n.t('PASSWORD_MUST_CONTAIN_KEY')}:
          </Typography>
          <ul className={classes.listRestrictions}>
            <li className={restrictionsMap.REQUIRE_UPPERCASE ? 'valid' : ''}>
              {i18n.t('REQUIRED_LETTER_UPPERCASE_KEY')}
            </li>
            <li className={restrictionsMap.REQUIRE_LOWERCASE ? 'valid' : ''}>
              {i18n.t('REQUIRED_LETTER_LOWERCASE_KEY')}
            </li>
            <li className={restrictionsMap.REQUIRE_NUMBER ? 'valid' : ''}>
              {i18n.t('REQUIRED_LETTER_TO_NUMBER_KEY')}
            </li>
            <li className={restrictionsMap.REQUIRE_SYMBOL ? 'valid' : ''}>
              {i18n.t('REQUIRED_LETTER_SPECIAL_CHARACTER_KEY')}
            </li>
            <li className={restrictionsMap.REQUIRE_MIN_LENGTH ? 'valid' : ''}>
              {i18n.t('REQUIRE_MIN_LENGTH_TEXT_KEY', {
                min_length: restrictions.min_length,
              })}
            </li>
            <li className={restrictionsMap.REQUIRE_MAX_LENGTH ? 'valid' : ''}>
              {i18n.t('REQUIRE_MAX_LENGTH_TEXT_KEY', {
                max_length: restrictions.max_length,
              })}
            </li>
          </ul>
        </Grid>
      </Grid>
      <Formik
        initialValues={init}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, errors, values }) => (
          <Form>
            <Grid container justify="center">
              <Grid
                item
                container
                xs={12}
                spacing={2}
                className={classes.password}
              >
                <GenericForm
                  values={values}
                  screenFields={applyI18n(screenFields, i18n.language)}
                  setFieldValue={(key, value) => {
                    if (key === 'password') {
                      checkRestrictions(value?.toString()!);
                    }
                    setFieldValue(key, value);
                  }}
                  errors={errors}
                />
              </Grid>
              <Grid
                container
                justify="flex-end"
                item
                xs={12}
                className={classes.forgotPasswordContainer}
              >
                <Grid item className={classes.forgotPasswordLinkContainer}>
                  <Typography
                    id="forgot-link"
                    variant="button"
                    component={Link}
                    to="/forgotPassword"
                    className={classes.forgotPasswordLinkText}
                  >
                    {i18n.t('FORGOT_PASSWORD_KEY')}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item xs={12} className={classes.spacingContainer}>
                <Button
                  fullWidth
                  size="large"
                  type="submit"
                  className={classes.loginButton}
                  id="submit-button"
                  isLoading={isLoading}
                >
                  {i18n.t('CHANGE_KEY')}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { isLoading, restrictions, isSuccess },
}: ApplicationState) => ({
  isLoading,
  isSuccess,
  restrictions,
});

export default connect(mapStateToProps, {
  registerPasswordRequest,
  authCheckRestrictionsRequest,
})(RegisterPasswordForm);
